/**
 * The dorm re-layout's PURE config functions: seeding the wizard controls from a
 * saved `dormConfig`, and building the `dormConfig` sent to
 * `optimizeDormLayout`.
 *
 * They live here rather than in `DormRelayoutModal.jsx` because that component
 * imports `../firebase`, which calls `getAuth()` at MODULE SCOPE and throws
 * `auth/invalid-api-key` on any machine without env — CI has none, so nothing in
 * that file is reachable from jest. Same rule as `3d/geometry/floorGeometry.js`
 * and `materialRegions.js`: pure logic stays importable with no environment.
 * The component re-exports all of it, so existing call sites are unchanged.
 */

/**
 * Must these occupants share a bunk?
 *
 * A TRIPLE IS ONLY REPRESENTABLE AS A BUNK PLUS ONE MORE BED.
 * `TRIPLE_BED_CONFIGURATIONS` is the only arrangement table with room for a
 * third occupant and every entry in it is a bunk plus one bed; three separate
 * beds falls through to the TWO-bed table, where P3 appears in no config, so
 * their bed is dropped before placement and is not even reported unplaced.
 * Measured in a 30x30 room: two of three beds, so it is not a fit problem.
 * Missing table tracked as #2386.
 *
 * DERIVED at every point of use rather than written into state — forcing a
 * `bunkMode` flag true left a double silently bunked after 3 -> 2.
 */
export function bunkRequiredFor(occupants) {
  return occupants >= 3;
}

/** Whether a bunk is in force: the student's answer, or the room's demand. */
export function bunkActiveFor(occupants, bunkMode) {
  return Boolean(bunkMode) || bunkRequiredFor(occupants);
}

// Mirrors `isBedKey` in functions/features/dorm-layout-generation.js — the
// selections keys the bed step owns and must replace on every re-generate.
const isBedKey = (key) =>
  key.startsWith('dorm-bed-') || key.startsWith('dorm-bunk-');

/**
 * Seed the wizard controls from the room's saved `dormConfig` so the student
 * starts from what they already have. Pure.
 *
 * Defaults match `parseBedConfig` on the server (`xl` / `standard` / `bed`), so
 * a config missing a field seeds the same value the optimizer would assume.
 */
export function seedFromDormConfig(dormConfig) {
  const cfg = dormConfig || {};
  const perPerson =
    Array.isArray(cfg.bedConfigs) && cfg.bedConfigs.length > 0
      ? cfg.bedConfigs
      : cfg.bedConfig
        ? [cfg.bedConfig]
        : [];
  const occupants = cfg.occupantCount || perPerson.length || 1;
  const first = perPerson[0] || {};
  return {
    goal: cfg.goal || '',
    occupants,
    // Per-person bed rows. `underBedClearanceInches` is carried through
    // untouched — the optimizer reads it per bed and there is no UI for it here.
    bedConfigs: Array.from({ length: occupants }, (_, i) => {
      const src = perPerson[i] || first;
      return {
        height: src.height || 'standard',
        underBedClearanceInches: src.underBedClearanceInches,
      };
    }),
    bedSize: first.size || 'xl',
    // The student's own answer only. The requirement at three is DERIVED by
    // `bunkActiveFor` wherever it matters (the Beds step's checkbox and
    // `buildRelayoutDormConfig`), so a saved broken triple still repairs itself
    // on re-layout without a forced value sitting in state.
    bunkMode: first.style === 'bunk',
  };
}

/**
 * Build the `dormConfig` sent to `optimizeDormLayout`. Same wire contract the
 * creation wizard builds (bed keys in `selections`, `bedConfig` +`bedConfigs`,
 * `goal` omitted entirely when Balanced) — with the room's own non-bed
 * selections and its other saved config fields (doorWall, personalItems, …)
 * carried over verbatim. Pure.
 */
export function buildRelayoutDormConfig({
  baseConfig,
  occupants,
  bedConfigs,
  bedSize,
  bunkMode,
  goal,
}) {
  const selections = {};
  const bunkActive = bunkActiveFor(occupants, bunkMode);
  const bunkPairCount = bunkActive ? Math.floor(occupants / 2) : 0;
  const soloStart = bunkActive ? bunkPairCount * 2 : 0;

  if (bunkActive) {
    for (let bi = 0; bi < bunkPairCount; bi++) {
      const height = bedConfigs[bi * 2]?.height || 'standard';
      const key = `dorm-bunk-${height}-${bedSize}`;
      selections[key] = { quantity: (selections[key]?.quantity || 0) + 1 };
    }
  }
  for (let i = soloStart; i < occupants; i++) {
    const height = bedConfigs[i]?.height || 'standard';
    const key = `dorm-bed-${height}-${bedSize}`;
    selections[key] = { quantity: (selections[key]?.quantity || 0) + 1 };
  }

  // Everything that is not a bed is the student's existing furniture choice —
  // this flow does not re-ask for it.
  for (const [key, value] of Object.entries(baseConfig?.selections || {})) {
    if (!isBedKey(key)) selections[key] = value;
  }

  // Style is PER PERSON — only the occupants actually sharing a bunk carry
  // 'bunk'. A room-level style is what made the odd occupant out of a bunked
  // triple come back as a copy of P1 (#2319).
  const serializedBedConfigs = bedConfigs.slice(0, occupants).map((cfg, i) => ({
    size: bedSize,
    height: cfg.height,
    style: i < soloStart ? 'bunk' : 'bed',
    ...(cfg.underBedClearanceInches != null
      ? { underBedClearanceInches: cfg.underBedClearanceInches }
      : {}),
  }));

  // Balanced = NO goal key at all (the server treats an unknown/absent goal as
  // default weights), so a saved goal must be dropped, not overwritten.
  //
  // Everything else on the saved config rides along by construction — notably
  // `scanInventory`, the student's per-object decisions about what the scan
  // found. Dropping it would silently revert the room to scan-blind on the next
  // web re-layout, and the symptom (furniture doubling) would show up nowhere
  // near this line.
  const carried = { ...(baseConfig || {}) };
  delete carried.goal;

  return {
    ...carried,
    occupantCount: occupants,
    bedConfig: serializedBedConfigs[0],
    bedConfigs: serializedBedConfigs,
    selections,
    ...(goal ? { goal } : {}),
  };
}

/**
 * The room's floor outline as `{x, z}` points, in the same world frame as the
 * walls and the returned layout items.
 *
 * Deliberately mirrors `prepareRoomData` in
 * shared-lib/dorm-optimizer-v2/pipeline.js — `floors[0].polygon`, else the wall
 * start points. `polygonCorners` is NOT consulted here precisely because the
 * optimizer does not consult it: the preview has to outline the same floor the
 * placement ran against.
 */
export function roomFloorPolygon(room, wallEndpoints) {
  const polygon = room?.floors?.[0]?.polygon;
  if (Array.isArray(polygon) && polygon.length >= 3) {
    return polygon.map((p) => ({ x: p.x, z: p.z }));
  }
  if (wallEndpoints.length >= 3) {
    return wallEndpoints.map((w) => ({ x: w.start.x, z: w.start.z }));
  }
  return null;
}
